import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useRef, useState } from 'react';

const TestimonialsSection = () => {

  const scrollRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonialsData = [
    {
      id: 1,
      testimonialText: "Working with Sentellent has transformed how we serve our community. Their team quickly grasped our unique challenges and developed a tailored Campus Insights AI solution that exceeded our expectations. I appreciate their commitment to bringing innovative technology to our institution at a fair price point.",
      authorName: "Dr. Monte Randall",
      authorTitle: "President, College of the Muscogee Nation",
      quoteIconWidth: "61",
      learnMoreLink: "/customer_stories"
    },
    {
      id: 2,
      testimonialText: "Campus Insights represents the next leap forward in how colleges connect with students. At CCPathways, we're using it to accelerate student learning journeys, giving every learner instant, personalized access to the information they need to make informed choices. Sentellent's team has built an AI solution that truly puts AI at the heart of every campus.",
      authorName: "Tennyson Williams",
      authorTitle: "CEO of CCPathways",
      quoteIconWidth: "53",
      learnMoreLink: "https://www.ccpathways.org/"
    },
    {
      id: 3,
      testimonialText: "I recently explored LXP and was genuinely impressed by its refined approach to course design and organization. The platform streamlines the creation of courses, quizzes, and assignments, enabling educators to save valuable time while upholding academic standards. What truly distinguishes it is its ability to transform learning materials into engaging formats such as podcasts, videos, and flashcards — enhancing accessibility and supporting diverse learning styles with elegance and efficiency.",
      authorName: "Pragati Dharmale",
      authorTitle: "CIS Faculty, San Jose City College",
      quoteIconWidth: "61",
      learnMoreLink: null
    },
    {
      id: 4,
      testimonialText: "As a math professor, I've had an incredibly positive experience with the LXP from Campus Insights. It helps me design structured lesson plans for complex subjects like calculus and statistics, breaking advanced concepts into clear, digestible modules. I especially value how it generates thoughtful discussion prompts that deepen student engagement. The AI insights save me time while maintaining academic rigor and creativity — it truly feels like a collaborative partner in course design.",
      authorName: "Dr. Maria Radhika Albert",
      authorTitle: "Professor of Mathematics, Merced Community College District",
      quoteIconWidth: "53",
      learnMoreLink: null
    }
  ];

  const total = testimonialsData.length;

  const handlePrev = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      scrollRef.current.scrollTo({ left: scrollRef.current.offsetWidth * newIndex, behavior: 'smooth' });
      setCurrentIndex(newIndex);
    }
  };

  const handleNext = () => {
    if (currentIndex < total - 1) {
      const newIndex = currentIndex + 1;
      scrollRef.current.scrollTo({ left: scrollRef.current.offsetWidth * newIndex, behavior: 'smooth' });
      setCurrentIndex(newIndex);
    }
  };

  return (
    <SectionWrapper>
      <HeaderSection>
        <TestimonialLabel>TESTIMONIAL</TestimonialLabel>
        <HeaderContent>
          <MainHeading>
            What Users Are Saying About Campus Insights
          </MainHeading>
        </HeaderContent>
      </HeaderSection>
      <CarouselWrapper>
        <NavButton onClick={handlePrev} disabled={currentIndex === 0} aria-label="Previous testimonial">
          <ChevronLeft />
        </NavButton>
        <TestimonialsContainer ref={scrollRef}>
          {testimonialsData.map((testimonial) => (
            <TestimonialCard
              key={testimonial.id}
              testimonialText={testimonial.testimonialText}
              authorName={testimonial.authorName}
              authorTitle={testimonial.authorTitle}
              quoteIconWidth={testimonial.quoteIconWidth}
              learnMoreLink={testimonial.learnMoreLink}
            />
          ))}
        </TestimonialsContainer>
        <NavButton onClick={handleNext} disabled={currentIndex === total - 1} aria-label="Next testimonial" $right>
          <ChevronRight />
        </NavButton>
      </CarouselWrapper>
    </SectionWrapper>
  );
};

const TestimonialCard = ({
  testimonialText,
  authorName,
  authorTitle,
  learnMoreLink,
  quoteIconWidth = "61"
}) => {

  const navigate = useNavigate();

  const handleBackClick = link => {
    if (!link) return;
    if (link.startsWith("http")) {
      window.open(link, "_blank");
    } else {
      window.scrollTo(0, 0);
      navigate(link);
    }
  };

  return (
    <CardContainer>
      <CardBackground />
      <GradientBackground />

      <QuoteIconWrapper>
        <QuoteIcon width={quoteIconWidth} />
      </QuoteIconWrapper>

      <ContentSection>
        <TestimonialContent>
          <blockquote className="testimonial-text">
            {testimonialText}
          </blockquote>
        </TestimonialContent>

        {learnMoreLink && (
          <CaseStudyWapper>
            <CaseStudyLink onClick={() => handleBackClick(learnMoreLink)}>
              Learn more
            </CaseStudyLink>
          </CaseStudyWapper>
        )}

        <AuthorSection>
          <AuthorInfo>
            <AuthorName>{authorName}</AuthorName>
            <AuthorPosition>{authorTitle}</AuthorPosition>
          </AuthorInfo>
          <Divider />
        </AuthorSection>
      </ContentSection>

      <StarDecorationLeft>
        <StarIcon width="215" height="206" fillColor="#6A37E3" fillOpacity="0.03" />
      </StarDecorationLeft>

      <StarDecorationRight>
        <StarIcon width="215" height="206" fillColor="white" fillOpacity="0.35" />
      </StarDecorationRight>

    </CardContainer>
  );
};

const StarIcon = ({ width = "215", height = "206", fillColor = "#6A37E3", fillOpacity = "0.03" }) => {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 215 206"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M94.6356 10.645C97.2501 -3.54832 117.591 -3.54834 120.205 10.645L130.309 65.4933C131.563 72.305 137.938 76.9368 144.805 76.0252L200.09 68.6848C214.397 66.7853 220.683 86.1307 207.992 93.0032L158.95 119.561C152.86 122.859 150.424 130.354 153.413 136.602L177.479 186.914C183.706 199.933 167.25 211.889 156.792 201.943L116.38 163.508C111.361 158.735 103.481 158.735 98.4616 163.508L58.0489 201.943C47.5912 211.889 31.135 199.933 37.3625 186.914L61.4279 136.602C64.4166 130.354 61.9816 122.859 55.8909 119.561L6.84914 93.0032C-5.84161 86.1307 0.44408 66.7853 14.7506 68.6848L70.0365 76.0252C76.9026 76.9368 83.2777 72.3051 84.5325 65.4933L94.6356 10.645Z"
        fill={fillColor}
        fillOpacity={fillOpacity}
      />
    </svg>
  );
};

const QuoteIcon = ({ width = "61", height = "51" }) => {
  return (
    <svg
      viewBox="0 0 61 51"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M24.7617 2.24023L28.4697 6.4541L29.2598 7.35059L28.333 8.10547C23.544 12.0035 19.8495 15.6493 17.2236 19.041C15.1825 21.6775 13.9386 23.9974 13.3877 26.0127C14.3526 25.598 15.5482 25.4453 16.8203 25.4453C19.6475 25.4453 22.0336 26.6195 23.918 28.8682C25.8229 31.0271 26.7344 33.7874 26.7344 37.0449C26.7344 40.5622 25.632 43.5106 23.3633 45.7793C21.0946 48.0479 18.1461 49.1504 14.6289 49.1504C10.7373 49.1503 7.53992 47.7961 5.16309 45.0439L5.12793 45.001C2.90511 42.1607 1.84969 38.4602 1.84961 34.0117C1.84961 27.7166 3.60353 21.9843 7.10645 16.8467C10.5937 11.6199 16.0087 6.68838 23.2783 2.03125L24.1104 1.49902L24.7617 2.24023Z"
        stroke="#1C1B1F"
        strokeWidth="2.29996"
      />
      <path
        d="M54.4373 2.24023L58.1453 6.4541L58.9353 7.35059L58.0085 8.10547C53.2196 12.0035 49.525 15.6493 46.8992 19.041C44.8581 21.6775 43.6141 23.9974 43.0632 26.0127C44.0282 25.598 45.2237 25.4453 46.4958 25.4453C49.323 25.4453 51.7092 26.6195 53.5935 28.8682C55.4984 31.0271 56.4099 33.7874 56.4099 37.0449C56.4099 40.5622 55.3075 43.5106 53.0388 45.7793C50.7701 48.0479 47.8216 49.1504 44.3044 49.1504C40.4128 49.1503 37.2155 47.7961 34.8386 45.0439L34.8035 45.001C32.5806 42.1607 31.5252 38.4602 31.5251 34.0117C31.5251 27.7166 33.2791 21.9843 36.782 16.8467C40.2692 11.6199 45.6842 6.68838 52.9539 2.03125L53.7859 1.49902L54.4373 2.24023Z"
        stroke="#1C1B1F"
        strokeWidth="2.29996"
      />
    </svg>
  );
};

const ChevronLeft = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRight = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const HeaderSection = styled.header`
  display: flex;
  width: 100%;
  max-width: 1037px;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 60px 53px 20px;
  box-sizing: border-box;

  @media (max-width: 640px) {
    padding: 40px 20px 16px;
  }
`;

const TestimonialLabel = styled.span`
  width: 100%;
  color: #1c1b1f;
  text-align: center;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 700 16px/120% Inter, sans-serif;

  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

const MainHeading = styled.p`
  width: 100%;
  max-width: 717px;
  color: #1c1b1f;
  text-align: center;
  letter-spacing: -0.48px;
  font: 700 48px/120% Inter, sans-serif;
  margin: 0;

  @media (max-width: 1021px) {
    font: 700 28px/120% Inter, sans-serif;
  }
  @media (max-width: 640px) {
    font-size: 28px;
    line-height: 36px;
    letter-spacing: -0.28px;
  }
`;

const CardContainer = styled.article`
  flex: 0 0 auto;
  min-width: 70%;
  max-width: 600px;
  scroll-snap-align: center;
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  position: relative;
  background-color: #fff;
  margin-left: 80px;
  padding: 40px 71px 40px;
  box-sizing: border-box;

  @media (max-width: 991px) {
    padding: 40px 40px;
  }
  @media (max-width: 640px) {
    min-width: 100%;
    padding: 30px 20px;
    margin-left: 0;
  }
`;

const CardBackground = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  border-radius: 16px;
`;

const GradientBackground = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 16px;
  background: conic-gradient(
    from 180deg at 50% 59.61%,
    #fff 0deg,
    #e4e1f8 90deg,
    #fff 180.54433822631836deg,
    #dee8fa 270.5781555175781deg,
    #fff 360deg
  );
  position: absolute;
  left: 0;
  top: 0;
`;

const QuoteIconWrapper = styled.div`
  width: 51px;
  height: 43px;
  position: relative;
  z-index: 1;
  margin-bottom: 16px;

  @media (max-width: 991px) {
    width: 41px;
    height: 35px;
  }
  @media (max-width: 640px) {
    width: 31px;
    height: 25px;
  }
`;

const ContentSection = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
  position: relative;
  z-index: 1;
  flex: 1;
`;

const TestimonialContent = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  position: relative;

  .testimonial-text {
    width: 100%;
    color: #11003b;
    letter-spacing: -0.036px;
    position: relative;
    font: 400 18px/165% Inter, sans-serif;
    margin: 0;
    border: none;

    @media (max-width: 991px) {
      font-size: 16px;
    }
    @media (max-width: 640px) {
      font-size: 14px;
      line-height: 150%;
    }
  }
`;

const AuthorSection = styled.div`
  display: flex;
  width: 100%;
  max-width: 891px;
  flex-direction: column;
  align-items: flex-start;
  gap: 21px;
  position: relative;
  margin-top: auto;
`;

const AuthorInfo = styled.div`
  display: flex;
  width: 100%;
  max-width: 337px;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  position: relative;
`;

const AuthorName = styled.h3`
  color: #1c1b1f;
  letter-spacing: -0.17px;
  position: relative;
  font: 700 17px/140% Inter, sans-serif;
  margin: 0;

  @media (max-width: 640px) {
    font-size: 15px;
  }
`;

const AuthorPosition = styled.p`
  color: #1c1b1f;
  letter-spacing: -0.085px;
  opacity: 0.8;
  position: relative;
  font: 400 17px/140% Inter, sans-serif;
  margin: 0;

  @media (max-width: 640px) {
    font-size: 15px;
  }
`;

const Divider = styled.hr`
  width: 100%;
  height: 1px;
  position: relative;
  background-color: #c4c4c4;
  border: none;
  margin: 0;

  @media (max-width: 991px) {
    display: none;
  }
`;

const StarDecorationLeft = styled.div`
  position: absolute;
  left: -50px;
  bottom: 50px;
  z-index: 1;

  @media (max-width: 991px) {
    display: none;
  }
`;

const StarDecorationRight = styled.div`
  position: absolute;
  right: -50px;
  top: 100px;
  z-index: 1;

  @media (max-width: 991px) {
    display: none;
  }
`;

const CaseStudyWapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
`;

const CaseStudyLink = styled.button`
  color: #6a37e3;
  background: none;
  border: none;
  text-align: right;
  letter-spacing: -0.075px;
  text-decoration-line: underline;
  position: relative;
  cursor: pointer;
  font: 700 15px/140% Inter, sans-serif;
`;

const CarouselWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
`;

const NavButton = styled.button`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 2px solid #6a37e3;
  background: #fff;
  color: #6a37e3;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: background 0.2s, color 0.2s, opacity 0.2s;
  position: relative;
  margin: 0 8px;

  &:hover:not(:disabled) {
    background: #6a37e3;
    color: #fff;
  }

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }

  @media (max-width: 640px) {
    display: none;
  }
`;

const TestimonialsContainer = styled.section`
  display: flex;
  flex-direction: row;
  gap: 20px;
  overflow-x: auto;
  width: 100%;
  padding: 30px 50px 50px;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  box-sizing: border-box;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #ccc;
    border-radius: 4px;
  }

  @media (max-width: 991px) {
    padding: 20px 10px 30px;
  }
`;


export default TestimonialsSection;
